import React, { useMemo, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { BRAND_OPTIONS } from '../../config/configUser';

import { H3, Page, LayoutSingleColumn, NamedLink } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './BrandsPage.module.css';

// Group brands by the first character of their display label; anything
// not A–Z (numbers, symbols) files under '#', listed first like most
// brand directories do.
const groupBrands = brands => {
  const groups = new Map();
  for (const brand of brands) {
    const first = (brand.label || '').charAt(0).toUpperCase();
    const letter = /[A-Z]/.test(first) ? first : '#';
    if (!groups.has(letter)) groups.set(letter, []);
    groups.get(letter).push(brand);
  }
  const letters = Array.from(groups.keys()).sort((a, b) => {
    if (a === '#') return -1;
    if (b === '#') return 1;
    return a.localeCompare(b);
  });
  return letters.map(letter => ({
    letter,
    brands: groups.get(letter).sort((a, b) => a.label.localeCompare(b.label)),
  }));
};

const ALPHABET = '#ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/**
 * Public A–Z directory of every brand on the marketplace (à la
 * materialbank.com/brands). Entirely static from BRAND_OPTIONS —
 * the same list that powers the brand search filter — so a new brand
 * appears here with the same one-edit that adds it to the filter.
 * Each entry lands on the brand's server-side filtered search results.
 */
export const BrandsPageComponent = props => {
  const { scrollingDisabled } = props;
  const intl = useIntl();
  const sectionRefs = useRef({});

  const title = intl.formatMessage({
    id: 'BrandsPage.title',
    defaultMessage: 'Brands',
  });

  const groups = useMemo(() => groupBrands(BRAND_OPTIONS), []);
  const activeLetters = useMemo(() => new Set(groups.map(g => g.letter)), [groups]);

  const scrollToLetter = letter => {
    const node = sectionRefs.current[letter];
    if (node) {
      const y = node.getBoundingClientRect().top + window.scrollY - 140;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.header}>
            <H3 as="h1" className={css.pageTitle}>
              <FormattedMessage id="BrandsPage.heading" defaultMessage="Brands" />
            </H3>
          </div>

          <div className={css.letterBar}>
            {ALPHABET.map(letter => (
              <button
                key={letter}
                type="button"
                className={classNames(css.letterButton, {
                  [css.letterButtonDisabled]: !activeLetters.has(letter),
                })}
                disabled={!activeLetters.has(letter)}
                onClick={() => scrollToLetter(letter)}
              >
                {letter}
              </button>
            ))}
          </div>

          {groups.map(group => (
            <section
              key={group.letter}
              className={css.letterSection}
              ref={node => {
                sectionRefs.current[group.letter] = node;
              }}
            >
              <h2 className={css.letterHeading}>{group.letter}</h2>
              <div className={css.brandGrid}>
                {group.brands.map(brand => (
                  <NamedLink
                    key={brand.option}
                    name="SearchPage"
                    to={{ search: `?pub_brand_slug=${brand.option}` }}
                    className={css.brandCard}
                  >
                    <span className={css.brandName}>{brand.label}</span>
                    <span className={css.brandArrow} aria-hidden="true">
                      →
                    </span>
                  </NamedLink>
                ))}
              </div>
            </section>
          ))}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const BrandsPage = compose(connect(mapStateToProps))(BrandsPageComponent);

export default BrandsPage;
